import React, { useState, useEffect } from 'react';
import userService from '../services/userService';
import type { User } from '../services/userService';

/**
 * Users page component
 */
const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const data = await userService.getUsers();
        setUsers(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const searchTermLower = searchTerm.toLowerCase();
    
    // Check username and email
    if (
      user.username.toLowerCase().includes(searchTermLower) ||
      user.email.toLowerCase().includes(searchTermLower)
    ) {
      return true;
    }
    
    // Check tags
    if (user.tags) {
      if (Array.isArray(user.tags)) {
        // If tags is an array, use some() to check if any tag includes the search term
        return user.tags.some((tag: string) => tag.toLowerCase().includes(searchTermLower));
      } else if (typeof user.tags === 'string') {
        // If tags is a string, check if it includes the search term
        try {
          // Try to parse it as JSON if it looks like an array
          if (user.tags.startsWith('[') && user.tags.endsWith(']')) {
            const parsedTags = JSON.parse(user.tags);
            if (Array.isArray(parsedTags)) {
              return parsedTags.some((tag: string) => tag.toLowerCase().includes(searchTermLower));
            }
          }
          // Otherwise just check the string itself
          return user.tags.toLowerCase().includes(searchTermLower);
        } catch (e) {
          // If parsing fails, just check the string itself
          return user.tags.toLowerCase().includes(searchTermLower);
        }
      }
    }
    
    return false;
  });

  return (
    <div className="page-container">
      <h1 className="page-title">Users</h1>
      
      <div className="card">
        <div className="flex justify-between items-center mb-lg">
          <div className="flex items-center">
            <input 
              type="text" 
              placeholder="Search users..." 
              className="p-sm"
              value={searchTerm}
              onChange={handleSearch}
              style={{ 
                width: '250px',
                borderRadius: 'var(--border-radius-sm)'
              }}
            />
          </div>
          <div className="flex items-center">
            <span className="mr-sm">Total: {users.length} users</span>
          </div>
        </div>
        
        {loading ? (
          <div className="flex flex-col items-center justify-center p-lg">
            <div className="spinner"></div>
            <p className="mt-md">Loading users...</p>
          </div>
        ) : error ? (
          <div className="bg-error p-md" style={{ borderRadius: 'var(--border-radius-md)' }}>
            <h2 className="text-white mb-sm" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'bold' }}>Error!</h2>
            <p className="text-white">{error}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Created Date</th>
                  <th>Last Synced</th>
                  <th>Tags</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center p-md">
                      No users match your search criteria
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.userId}>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>{new Date(user.createdDate).toLocaleDateString()}</td>
                      <td>{new Date(user.syncedDate).toLocaleDateString()}</td>
                      <td>
                        <div className="flex flex-wrap gap-xs">
                          {user.tags && (() => {
                            // Handle different formats of tags
                            let tagsArray: string[] = [];
                            
                            if (Array.isArray(user.tags)) {
                              tagsArray = user.tags;
                            } else if (typeof user.tags === 'string') {
                              // Try to parse JSON string if it looks like an array
                              try {
                                if (user.tags.startsWith('[') && user.tags.endsWith(']')) {
                                  tagsArray = JSON.parse(user.tags);
                                } else {
                                  tagsArray = [user.tags]; // Single tag as string
                                }
                              } catch (e) {
                                console.error('Error parsing tags:', e);
                                tagsArray = [user.tags]; // Fallback to treating as single tag
                              }
                            }
                            
                            return tagsArray.map((tag, index) => (
                              <span 
                                key={index} 
                                className="tag"
                                style={{
                                  backgroundColor: 'var(--color-primary-light)',
                                  color: 'var(--color-primary-dark)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}
                              >
                                {tag}
                              </span>
                            ));
                          })()}
                        </div>
                      </td>
                      <td>
                        <span 
                          style={{ 
                            color: user.isSuspended ? 'var(--color-error)' : 'var(--color-success)',
                            fontWeight: 'bold'
                          }}
                        >
                          {user.isSuspended ? 'Suspended' : 'Active'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
